import {
  Anchor,
  Button,
  ButtonGroup,
  DetailedIconPaperAirplane,
  Heading,
  List,
  ModalDialog,
  Paragraph,
  TextButton,
  useToast,
} from '@zillow/constellation';
import { useEffectOnce } from 'react-use';
import { FAIR_HOUSING_URL } from '../../constants/urls';
import type { UserStatus } from '../../graphql/queries';
import { getDaysLeftOnPayment } from '../../utils/user-statuses';
import { pluralize } from '../../utils/formatters';
import type { SubmittedApplication } from '../../contexts/BaseContext';
import { SUBMISSION_TYPES } from '../../contexts/BaseContext';
import { usePaymentRequestTreatment } from '../../utils/ab-tests';
import { useState } from 'react';
import { PaymentRequestToast, PaymentsAndReportsUpsellBox } from './Sections/6Reports/1PaymentUpsellTip';
import {
  emitPaymentUpsellConfirmModalImpressionEvent,
  emitPaymentUpsellConfirmModalInteractionEvent,
} from '../../analytics/clickstreamEvents';
import { useOptInOutRenterRequestPayment, useRenterRequestedZillowPayments } from '../../utils/rentPaymentQueries';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

interface Props {
  onClose: () => void;
  userStatuses: Array<UserStatus>;
  submittedApplication: SubmittedApplication;
}

export function ApplicationSubmitted({ onClose, userStatuses, submittedApplication }: Props) {
  const daysLeftOnPayment = getDaysLeftOnPayment(userStatuses);
  const paymentRequestTreatment = usePaymentRequestTreatment();
  const requestedZillowPaymentsOriginal = useRenterRequestedZillowPayments(submittedApplication.applicationId, {
    enabled: paymentRequestTreatment != null && paymentRequestTreatment !== 'off',
  });
  const [requestedZillowPaymentsNow, setRequestedZillowPaymentsNow] = useState<boolean | null>(null);
  const optInOutPayments = useOptInOutRenterRequestPayment(
    submittedApplication.applicationId,
    submittedApplication.listingAlias,
  );

  const requestedZillowPayments =
    requestedZillowPaymentsNow != null ? requestedZillowPaymentsNow : requestedZillowPaymentsOriginal;
  const showUpsell = paymentRequestTreatment === 'on_3' && requestedZillowPayments == null;

  const { enqueueToast } = useToast();

  const handleOptOutPayments = async () => {
    await optInOutPayments(false);
    await setRequestedZillowPaymentsNow(false);
  };
  const handleOptInPayments = async () => {
    await optInOutPayments(true);
    await setRequestedZillowPaymentsNow(true);
    enqueueToast(<PaymentRequestToast onUndoRequestPayments={handleOptOutPayments} />);
  };

  return (
    <ModalDialog
      isOpen
      onClose={onClose}
      size={{ default: 'fullScreen', md: 'lg' }}
      renderBody={
        <>
          <DetailedIconPaperAirplane />
          <Heading level={4} as="h1" marginY="sm">
            Congrats! You {submittedApplication.type === SUBMISSION_TYPES.SUBMITTED ? `submitted` : `shared`} your
            application
          </Heading>
          <Heading level={6} as="h2">
            Next steps
          </Heading>
          <Paragraph>
            <List marginBottom="xs">
              <List.Item>
                The rental manager will take it from here. You can reach out to them about next steps.
              </List.Item>
              {daysLeftOnPayment > 0 && (
                <List.Item>
                  You can reuse this application at participating properties for the next{' '}
                  {pluralize('day', daysLeftOnPayment)}.
                </List.Item>
              )}
              {requestedZillowPayments && (
                <List.Item>
                  We’ll send a request for Zillow payments to your landlord if your application is accepted.
                </List.Item>
              )}
            </List>
          </Paragraph>
          {showUpsell && (
            <PaymentsAndReportsUpsellBox
              sx={{
                marginTop: '$spacing.sm',
                marginBottom: '$spacing.sm',
              }}
            />
          )}
          <Paragraph fontColor="textMedium" fontType="bodySmall">
            Zillow is committed to fair, inclusive housing. Know and exercise your{' '}
            <Anchor href={FAIR_HOUSING_URL} target="_blank" rel="noopener noreferrer">
              {' '}
              fair housing rights
            </Anchor>
            .
          </Paragraph>
        </>
      }
      renderFooter={
        showUpsell ? (
          <ButtonGroup aria-label="Request Zillow payments actions">
            <TextButton type="button" onClick={() => handleOptOutPayments}>
              Not now
            </TextButton>
            <RequestZillowPaymentsButton onClick={handleOptInPayments} />
          </ButtonGroup>
        ) : (
          <Button buttonType="primary" onClick={onClose}>
            Go to your dashboard
          </Button>
        )
      }
    />
  );
}

export function RequestZillowPaymentsButton({ onClick }: { onClick: () => void }) {
  const applicationContext = useApplicationContextOrUndefined();

  useEffectOnce(() => {
    emitPaymentUpsellConfirmModalImpressionEvent(applicationContext);
  });

  const handleClick = () => {
    emitPaymentUpsellConfirmModalInteractionEvent(applicationContext);
    onClick();
  };

  return (
    <Button buttonType="primary" onClick={handleClick}>
      Request Zillow payments
    </Button>
  );
}
